import React from "react";
import { JobSkill } from "../model/JobModel";
import { ProficiencyLevel } from "../model/jobEnums";

interface SkillsSectionProps {
  skills: JobSkill[];
  newSkill: JobSkill;
  onAddSkill: () => void;
  onDeleteSkill: (skillId: string) => void;
  onNewSkillChange: (skill: JobSkill) => void;
  onUpdateSkill: (skillId: string, updates: Partial<JobSkill>) => void;
}

const SkillsSection: React.FC<SkillsSectionProps> = ({
  skills,
  newSkill,
  onAddSkill,
  onDeleteSkill,
  onNewSkillChange,
  onUpdateSkill,
}) => {
  return (
    <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
      <h4 className="font-medium text-blue-900 mb-3">Add New Skill</h4>
      <div className="flex flex-col md:flex-row md:items-center md:space-x-4 space-y-3 md:space-y-0">
        {/* Required Checkbox */}
        <div className="flex items-center space-x-2 w-auto mt-4">
          <input
            type="checkbox"
            checked={newSkill.is_required}
            onChange={(e) => {
              onNewSkillChange({ ...newSkill, is_required: e.target.checked });
            }}
            className="h-4 w-4"
          />
          <label className="text-sm text-blue-700">Required</label>
        </div>

        {/* Skill Name */}
        <div className="flex-1">
          <label className="block text-sm font-medium text-blue-700 mb-1">
            Skill Name
          </label>
          <input
            type="text"
            value={newSkill.skill_name}
            onChange={(e) =>
              onNewSkillChange({ ...newSkill, skill_name: e.target.value })
            }
            className="w-full border rounded-md px-2 py-1"
            placeholder="e.g., JavaScript"
          />
        </div>

        {/* Proficiency */}
        <div className="flex-1">
          <label className="block text-sm font-medium text-blue-700 mb-1">
            Proficiency Level
          </label>
          <select
            value={newSkill.proficiency_level}
            onChange={(e) =>
              onNewSkillChange({
                ...newSkill,
                proficiency_level: e.target.value,
              })
            }
            className="w-full border rounded-md px-2 py-1"
          >
            <option value={ProficiencyLevel.Beginner}>
              {ProficiencyLevel.Beginner}
            </option>
            <option value={ProficiencyLevel.Intermediate}>
              {ProficiencyLevel.Intermediate}
            </option>
            <option value={ProficiencyLevel.Advanced}>
              {ProficiencyLevel.Advanced}
            </option>
            <option value={ProficiencyLevel.Expert}>
              {ProficiencyLevel.Expert}
            </option>
          </select>
        </div>

        {/* Years of Experience */}
        <div className="flex-1">
          <label className="block text-sm font-medium text-blue-700 mb-1">
            Years of Experience
          </label>
          <input
            type="number"
            min="0"
            value={newSkill.minimum_years}
            onChange={(e) =>
              onNewSkillChange({
                ...newSkill,
                minimum_years: parseInt(e.target.value) || 0,
              })
            }
            className="w-full border rounded-md px-2 py-1"
          />
        </div>
      </div>

      {/* Buttons */}
      <div className="mt-4 flex space-x-3">
        <button
          type="button"
          onClick={onAddSkill}
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Add Skill
        </button>

        <button
          type="button"
          onClick={() =>
            onNewSkillChange({
              skill_name: "",
              is_required: false,
              minimum_years: 0,
              proficiency_level: "beginner",
            })
          }
          className="px-4 py-2 border rounded-md"
        >
          Cancel
        </button>
      </div>

      <div className="space-y-4 mt-4">
        {skills.map((skill, i) => (
          <div
            key={skill.skill_id || i}
            className="p-4 bg-gray-50 rounded-lg border border-gray-200"
          >
            <div className="grid grid-cols-5 gap-4 items-center">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={skill.is_required}
                  className="h-4 w-4"
                  onChange={(e) =>
                    onUpdateSkill(skill.skill_id, {
                      is_required: e.target.checked,
                    })
                  }
                />
                <label className="text-sm text-blue-700">Required</label>
              </div>
              <div className="">
                <label className="block text-sm font-medium text-gray-700">
                  Skill Name
                </label>
                <input
                  type="text"
                  value={skill.skill_name}
                  onChange={(e) =>
                    onUpdateSkill(skill.skill_id, {
                      skill_name: e.target.value,
                    })
                  }
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-emerald-500 focus:ring-emerald-500"
                />
              </div>

              <div className="">
                <label className="block text-sm font-medium text-gray-700">
                  Proficiency
                </label>
                <select
                  value={skill.proficiency_level}
                  onChange={(e) =>
                    onUpdateSkill(skill.skill_id, {
                      proficiency_level: e.target.value,
                    })
                  }
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-emerald-500 focus:ring-emerald-500"
                >
                  <option value="beginner">Beginner</option>
                  <option value="intermediate">Intermediate</option>
                  <option value="advanced">Advanced</option>
                  <option value="expert">Expert</option>
                </select>
              </div>

              <div className="">
                <label className="block text-sm font-medium text-gray-700">
                  Years Experience
                </label>
                <input
                  type="number"
                  min="0"
                  value={skill.minimum_years || 0}
                  onChange={(e) =>
                    onUpdateSkill(skill.skill_id, {
                      minimum_years: parseInt(e.target.value) || 0,
                    })
                  }
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-emerald-500 focus:ring-emerald-500"
                />
              </div>
              <div className="flex items-center justify-center pb-1">
                <button
                  type="button"
                  onClick={() => onDeleteSkill(skill.skill_id)}
                  className="ml-4 p-2 text-red-600 hover:text-red-800"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        ))}

        {skills.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            <p>No skills added yet. Click "Add Skill" to get started.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SkillsSection;
